// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md

import * as React from 'react';
import { Upload } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { NOT_OAUTH_CLIENT_ERROR, parseGoogleOAuthClientFile } from '@/lib/googleOAuthClientFile';
import { cn } from '@/lib/utils';

export interface GoogleClientFileImportProps {
  /** The install's OIDC callback URL, checked against the file's redirect URIs. */
  redirectUri: string;
  onImport: (values: { clientId: string; clientSecret: string }) => void;
}

function readText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

type ImportStatus =
  | { kind: 'idle' }
  | { kind: 'imported'; fileName: string; missingRedirectUri: string | null }
  | { kind: 'refused'; error: string };

/**
 * File picker and drop target for the OAuth client JSON Google Cloud downloads.
 * The file is read in the browser and only fills the Client ID and secret
 * fields; nothing reaches the server until the surface saves.
 */
export function GoogleClientFileImport({
  redirectUri,
  onImport,
}: GoogleClientFileImportProps): React.JSX.Element {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [over, setOver] = React.useState(false);
  const [status, setStatus] = React.useState<ImportStatus>({ kind: 'idle' });

  const readFile = async (file: File | undefined | null): Promise<void> => {
    if (!file) return;
    let text: string;
    try {
      text = await readText(file);
    } catch {
      setStatus({ kind: 'refused', error: NOT_OAUTH_CLIENT_ERROR });
      return;
    }
    const result = parseGoogleOAuthClientFile(text, redirectUri);
    if (!result.ok) {
      setStatus({ kind: 'refused', error: result.error });
      return;
    }
    onImport({ clientId: result.clientId, clientSecret: result.clientSecret });
    setStatus({
      kind: 'imported',
      fileName: file.name,
      missingRedirectUri: result.missingRedirectUri,
    });
  };

  return (
    <div className="space-y-2" data-testid="idp-google-json-import">
      <span className="text-sm font-medium">Import JSON from Google Cloud</span>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          void readFile(e.dataTransfer?.files?.[0]);
        }}
        className={cn(
          'flex w-full min-h-11 items-center gap-3 rounded-lg border border-dashed p-3 text-left text-sm transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          over ? 'border-primary bg-muted' : 'hover:border-primary/60',
        )}
        data-testid="idp-google-json-drop"
      >
        <Upload className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="text-muted-foreground">
          Choose or drop the <span className="font-mono">client_secret_….json</span> file you
          downloaded when you created the OAuth client. It fills the Client ID and secret below.
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".json,application/json"
        hidden
        onChange={(e) => {
          void readFile(e.target.files?.[0]);
          e.target.value = '';
        }}
        data-testid="idp-google-json-input"
      />

      {status.kind === 'refused' && (
        <p className="text-sm text-destructive" role="alert" data-testid="idp-google-json-error">
          {status.error}
        </p>
      )}
      {status.kind === 'imported' && (
        <p className="text-xs text-muted-foreground" data-testid="idp-google-json-imported">
          Filled from <span className="font-mono break-all">{status.fileName}</span>.
        </p>
      )}
      {status.kind === 'imported' && status.missingRedirectUri && (
        <Alert data-testid="idp-google-json-redirect-warning">
          <AlertDescription className="space-y-2">
            <p>
              This client doesn't list this install's redirect URI, so sign-in will fail with{' '}
              <span className="font-mono">redirect_uri_mismatch</span>. In Google Cloud, add it
              under Authorized redirect URIs:
            </p>
            <MonoValue copyable wrap data-testid="idp-google-json-missing-uri">
              {status.missingRedirectUri}
            </MonoValue>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
